import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import useEmblaCarousel from 'embla-carousel-react';
import { EmblaCarouselType } from 'embla-carousel';
import { PlayerAvatar } from './player/PlayerAvatar';
import { LeagueLogo } from './LeagueLogo';
import StandingsBarList from './StandingsBarList';

interface LeagueAssignment {
  player: any;
  assignment: any;
}

interface LeaguePerformanceCarouselProps {
  assignments: LeagueAssignment[];
  standingsData: Record<string, any>;
}

const LeaguePerformanceCarousel: React.FC<LeaguePerformanceCarouselProps> = ({
  assignments,
  standingsData
}) => {
  // 1 assignment per page
  const pages = assignments.map(a => [a]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: pages.length > 1,
    align: 'start',
    watchDrag: pages.length > 1
  });
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onPrevButtonClick = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const onNextButtonClick = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const onDotButtonClick = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box ref={emblaRef} sx={{ overflow: 'hidden', flex: 1, minHeight: 0, userSelect: pages.length > 1 ? 'none' : 'auto' }}>
        <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
          {pages.map((page, pageIndex) => (
            <Box key={pageIndex} sx={{
              flex: '0 0 calc(100% - 8px)',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              pl: 0.5,
              mr: 1,
              overflowY: 'auto',
            }}>
              {page.map(({ player, assignment }) => {
                const data = standingsData[assignment.id];
                const contextual = data?.contextualStandings;

                return (
                  <Box key={`${player.id}-${assignment.id}`} sx={{ display: 'flex', flexDirection: 'column' }}>
                    {/* Header: logo + player avatar, player name, team, league */}
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      {(assignment.league as any).logoPath || assignment.league.logoUrl ? (
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                          <LeagueLogo
                            leagueName={assignment.league.name}
                            logoPath={(assignment.league as any).logoPath}
                            logoUrl={assignment.league.logoUrl}
                            size={50}
                          />
                          <Box sx={{ position: 'absolute', bottom: 5, right: -5 }}>
                            <PlayerAvatar
                              playerId={player.id}
                              player={player}
                              size="small"
                              sx={{ width: 25, height: 25, border: '2px solid white', borderRadius: '50%' }}
                            />
                          </Box>
                        </Box>
                      ) : (
                        <PlayerAvatar
                          playerId={player.id}
                          player={player}
                          size="small"
                          sx={{ width: 50, height: 50 }}
                        />
                      )}
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium', lineHeight: 1.3 }} title={player.name}>
                          {player.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }} title={assignment.teamName}>
                          {assignment.teamName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }} title={assignment.league.division ? `${assignment.league.name} - ${assignment.league.division}` : assignment.league.name}>
                          {assignment.league.division ? `${assignment.league.name} - ${assignment.league.division}` : assignment.league.name}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Standings bar list */}
                    {contextual && contextual.length > 0 ? (
                      <StandingsBarList
                        teams={contextual}
                        standingsSortBy={assignment.league.standingsSortBy}
                      />
                    ) : data?.teamData ? (
                      <StandingsBarList
                        teams={[{
                          position: data.teamData.position || assignment.position || 'N/A',
                          teamName: data.teamData.teamName || assignment.teamName,
                          won: data.teamData.won || 0,
                          drawn: data.teamData.drawn || 0,
                          lost: data.teamData.lost || 0,
                          points: data.teamData.points || 0,
                          played: data.teamData.played || 0,
                          isUserTeam: true,
                        }]}
                        standingsSortBy={assignment.league.standingsSortBy}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No standings data available
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>

      {pages.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} size="small"
              sx={{ width: 36, height: 36, border: '2px solid', borderColor: 'divider' }}>
              <ChevronLeft />
            </IconButton>
            <IconButton onClick={onNextButtonClick} disabled={nextBtnDisabled} size="small"
              sx={{ width: 36, height: 36, border: '2px solid', borderColor: 'divider' }}>
              <ChevronRight />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {pages.map((_, index) => (
              <IconButton key={index} onClick={() => onDotButtonClick(index)} size="small"
                sx={{ width: 26, height: 26, p: 0, '&::after': {
                  content: '""', width: 14, height: 14, borderRadius: '50%',
                  border: '2px solid', borderColor: index === selectedIndex ? 'text.primary' : 'divider',
                  display: 'block'
                }}}>
              </IconButton>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LeaguePerformanceCarousel;
